import React, { useRef, useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import {
  getAttack,
  getDecay,
  getSustain,
  getRelease,
  setAttack,
  setDecay,
  setSustain,
  setRelease,
} from "../utils/audioUtils";

interface ADSREnvelopeProps {
  width?: number;
  height?: number;
  waveformData?: number[];
  sampleDuration?: number; // Duration in seconds
}

interface ControlPoint {
  x: number;
  y: number;
  type: "attack" | "decay" | "sustain" | "release";
  dragging: boolean;
}

const ADSREnvelope: React.FC<ADSREnvelopeProps> = ({
  width = 480,
  height = 154,
  waveformData = [],
  sampleDuration = 1.0, // Default to 1 second for backward compatibility
}) => {
  // Add padding around the graph for easier clicking
  const padding = 10;
  const graphWidth = width - 2 * padding;
  const graphHeight = height - 2 * padding;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [controlPoints, setControlPoints] = useState<ControlPoint[]>([]);
  const [draggedPoint, setDraggedPoint] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const getSustainVisualOffset = useCallback((points: ControlPoint[]) => {
    const decayPoint = points.find((point) => point.type === "decay");
    const releasePoint = points.find((point) => point.type === "release");
    if (!decayPoint || !releasePoint) {
      return 0.03;
    }

    const maxOffset = Math.max(0, releasePoint.x - decayPoint.x - 0.01);
    return Math.min(0.03, maxOffset);
  }, []);

  // Initialize control points from ADSR values
  const initializeControlPoints = useCallback(() => {
    const attack = getAttack();
    const decay = getDecay();
    const sustain = getSustain();
    const release = getRelease();

    // Normalize ADSR times by sample duration
    // Time axis: 0 to 1 (representing 0 to sampleDuration seconds)
    // Amplitude axis: 0 to 1 (representing 0% to 100%)
    const normalizedAttack = Math.min(attack / sampleDuration, 0.8);
    const normalizedDecay = Math.min(decay / sampleDuration, 0.8);
    const normalizedRelease = Math.min(
      release / sampleDuration,
      1 - normalizedAttack - normalizedDecay
    );

    const decayX = Math.min(normalizedAttack + normalizedDecay, 0.95);
    const releaseX = Math.min(decayX + normalizedRelease, 1);

    const points: ControlPoint[] = [
      // Attack peak (attack time, 1)
      { x: normalizedAttack, y: 0, type: "attack", dragging: false },
      // Decay end (attack + decay, sustain level)
      {
        x: decayX,
        y: 1 - sustain, // Inverted: top = 100% sustain, bottom = 0% sustain
        type: "decay",
        dragging: false,
      },
      // Sustain point (same x as decay, sustain level)
      {
        x: decayX,
        y: 1 - sustain, // Inverted: top = 100% sustain, bottom = 0% sustain
        type: "sustain",
        dragging: false,
      },
      // Release end (attack + decay + release, 0)
      {
        x: releaseX,
        y: 1,
        type: "release",
        dragging: false,
      },
    ];

    setControlPoints(points);
  }, [sampleDuration]);

  // Update control points when ADSR values change
  useEffect(() => {
    initializeControlPoints();
  }, [initializeControlPoints]);

  // Continuously check for ADSR value changes from external sources (e.g., knob adjustments)
  // Use requestAnimationFrame for smooth, immediate updates
  // Skip checking while actively dragging to avoid interference
  useEffect(() => {
    if (isDragging) return; // Skip checking during drag operations

    let animationFrameId: number;
    let lastAttack = getAttack();
    let lastDecay = getDecay();
    let lastSustain = getSustain();
    let lastRelease = getRelease();

    const checkForChanges = () => {
      const currentAttack = getAttack();
      const currentDecay = getDecay();
      const currentSustain = getSustain();
      const currentRelease = getRelease();

      // Check if any values have changed (with threshold to avoid unnecessary updates)
      const threshold = 0.001;
      if (
        Math.abs(currentAttack - lastAttack) > threshold ||
        Math.abs(currentDecay - lastDecay) > threshold ||
        Math.abs(currentSustain - lastSustain) > threshold ||
        Math.abs(currentRelease - lastRelease) > threshold
      ) {
        // Values have changed externally (e.g., from knob adjustments), reinitialize
        lastAttack = currentAttack;
        lastDecay = currentDecay;
        lastSustain = currentSustain;
        lastRelease = currentRelease;
        initializeControlPoints();
        // The useEffect watching controlPoints will trigger a redraw automatically
      }

      // Continue checking on next frame
      animationFrameId = requestAnimationFrame(checkForChanges);
    };

    // Start the animation loop
    animationFrameId = requestAnimationFrame(checkForChanges);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [initializeControlPoints, isDragging]);

  // Helper function to calculate display time parameters
  const calculateDisplayTimeParams = useCallback(() => {
    // Add padding (20% of sample duration, minimum 50ms)
    const paddingAmount = Math.max(sampleDuration * 0.2, 0.05);
    const displayTimeRange = sampleDuration + paddingAmount;

    // Choose interval: 100ms or 200ms based on what makes sense
    // Use 100ms for shorter samples (< 0.5s), 200ms for longer samples
    const intervalMs = sampleDuration < 0.5 ? 100 : 200;
    const intervalSeconds = intervalMs / 1000;

    // Calculate how many intervals fit in the display range
    const numIntervals = Math.ceil(displayTimeRange / intervalSeconds);
    const actualDisplayRange = numIntervals * intervalSeconds;

    return {
      intervalSeconds,
      intervalMs,
      displayTimeRange: actualDisplayRange,
      numIntervals,
      sampleEndPosition: sampleDuration / actualDisplayRange, // Normalized position where sample ends
    };
  }, [sampleDuration]);

  // Helper function to draw envelope with specific points (for immediate feedback during dragging)
  const drawEnvelopeWithPoints = useCallback(
    (pointsToDraw: ControlPoint[]) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return;
      }

      // Calculate display parameters
      const timeParams = calculateDisplayTimeParams();
      const { intervalMs, numIntervals, sampleEndPosition } = timeParams;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw background grid
      ctx.strokeStyle = "#333";
      ctx.lineWidth = 0.5;

      // Vertical grid lines (time markers) - based on calculated intervals
      for (let i = 0; i <= numIntervals; i++) {
        const normalizedX = i / numIntervals;
        const x = padding + normalizedX * graphWidth;
        ctx.beginPath();
        ctx.moveTo(x, padding);
        ctx.lineTo(x, padding + graphHeight);
        ctx.stroke();
      }

      // Horizontal grid lines (amplitude markers) - centered in graph area
      for (let i = 0; i <= 4; i++) {
        const y = padding + (graphHeight * i) / 4;
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(padding + graphWidth, y);
        ctx.stroke();
      }

      // Draw time labels based on rounded intervals at the top
      // Limit the number of labels to avoid overcrowding on long samples
      const maxLabels = 12; // Maximum number of labels to display
      const totalLabels = numIntervals + 1;

      // Determine which labels to show
      let labelsToShow: number[];
      if (totalLabels <= maxLabels) {
        // Show all labels if we're under the limit
        labelsToShow = Array.from({ length: totalLabels }, (_, i) => i);
      } else {
        // Show first, last, and evenly distributed labels in between
        labelsToShow = [0]; // Always show first label

        // Calculate step size to distribute remaining labels
        const step = (numIntervals - 1) / (maxLabels - 2); // -2 because we're adding first and last

        // Add evenly distributed labels (excluding first and last)
        for (let i = 1; i < maxLabels - 1; i++) {
          const labelIndex = Math.round(i * step);
          if (labelIndex > 0 && labelIndex < numIntervals) {
            labelsToShow.push(labelIndex);
          }
        }

        labelsToShow.push(numIntervals); // Always show last label
        labelsToShow.sort((a, b) => a - b); // Ensure sorted order
      }

      ctx.fillStyle = "#FFFFFF"; // Bright white instead of gray
      ctx.font = "10px Arial";
      ctx.textAlign = "center";
      for (const i of labelsToShow) {
        const normalizedX = i / numIntervals;
        const x = padding + normalizedX * graphWidth;
        const labelMs = i * intervalMs;
        const timeLabel =
          labelMs > 1000
            ? `${parseFloat((labelMs / 1000).toFixed(1))}s`
            : `${labelMs}ms`;
        ctx.fillText(timeLabel, x, padding + 12); // Top of grid instead of bottom
      }

      // Draw waveform if data is available - scaled to sample duration portion only
      if (waveformData.length > 0) {
        // Calculate the width available for the sample (up to sampleEndPosition)
        const sampleWidth = graphWidth * sampleEndPosition;

        // Use smaller bars with gaps to fit more across the sample width
        const barGap = 0.7; // Smaller gap for more density

        // Calculate bar width to fill the sample width
        const totalGapWidth = (waveformData.length - 1) * barGap;
        const availableWidthForBars = sampleWidth - totalGapWidth;
        const barWidth = Math.max(
          1,
          availableWidthForBars / waveformData.length
        );

        // Start from the left edge of the graph area
        const startX = padding;

        // Find the maximum value in the waveform data for normalization
        const maxValue = Math.max(...waveformData, 0.01); // Avoid division by zero

        // Draw each bar, only up to the sample end position
        waveformData.forEach((value, index) => {
          // Normalize the value and calculate bar height (use 80% of graph height)
          const normalizedValue = value / maxValue;
          const barHeight = normalizedValue * (graphHeight * 0.8);

          // Calculate x position - scale to sample width only
          const normalizedIndex = index / waveformData.length;
          const x = startX + normalizedIndex * sampleWidth;
          const y = padding + (graphHeight - barHeight) / 2; // Center vertically in graph area

          // Only draw if within the sample duration area
          if (x + barWidth <= padding + sampleWidth) {
            // Draw the waveform bar
            ctx.fillStyle = "#7BC6F4"; // Blue color
            ctx.fillRect(x, y, barWidth, barHeight);
          }
        });
      }

      // Draw envelope line - scale to sample duration portion only
      if (pointsToDraw.length >= 4) {
        ctx.strokeStyle = "#FFD700"; // Yellow line
        ctx.lineWidth = 2;
        ctx.beginPath();

        const curvePoints = [
          { x: 0, y: 1 },
          ...pointsToDraw.map((point) => ({
            x: Math.min(point.x, 1.0),
            y: point.y,
          })),
        ];

        const toCanvasPoint = (point: { x: number; y: number }) => ({
          x: padding + point.x * sampleEndPosition * graphWidth,
          y: padding + point.y * graphHeight,
        });

        const start = toCanvasPoint(curvePoints[0]);
        ctx.moveTo(start.x, start.y);

        for (let i = 0; i < curvePoints.length - 1; i++) {
          const p1 = curvePoints[i];
          const p2 = curvePoints[i + 1];
          const p1Canvas = toCanvasPoint(p1);
          const p2Canvas = toCanvasPoint(p2);

          if (i === 0 || p1.x === p2.x || p1.y === p2.y) {
            ctx.lineTo(p2Canvas.x, p2Canvas.y);
            continue;
          }

          const controlX = (p1Canvas.x + p2Canvas.x) / 2;
          const controlY = Math.max(p1Canvas.y, p2Canvas.y);

          ctx.quadraticCurveTo(controlX, controlY, p2Canvas.x, p2Canvas.y);
        }

        ctx.stroke();
      }

      const sustainOffset = getSustainVisualOffset(pointsToDraw);

      // Draw control points
      pointsToDraw.forEach((point) => {
        const visualOffset = point.type === "sustain" ? sustainOffset : 0;

        // Scale x to sample duration portion
        const normalizedX = Math.min(point.x + visualOffset, 1.0);
        const x = padding + normalizedX * sampleEndPosition * graphWidth;
        const y = padding + point.y * graphHeight;

        // Draw point
        ctx.fillStyle = "#FFD700";
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, 2 * Math.PI);
        ctx.fill();

        // Draw border
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 1;
        ctx.stroke();
      });
    },
    [
      width,
      height,
      waveformData,
      graphWidth,
      graphHeight,
      calculateDisplayTimeParams,
      getSustainVisualOffset,
    ]
  );

  // Draw the envelope using current control points
  const drawEnvelope = useCallback(() => {
    drawEnvelopeWithPoints(controlPoints);
  }, [controlPoints, drawEnvelopeWithPoints]);

  // Update ADSR values from control point positions
  const updateADSRFromPoints = useCallback(
    (points: ControlPoint[]) => {
      if (points.length < 4) return;

      // Calculate normalized ADSR values from point positions (0-1 range)
      const normalizedAttack = points[0].x; // Attack peak x position
      const normalizedDecay = points[1].x - points[0].x; // Decay end - attack peak
      const sustain = 1 - points[2].y; // Sustain level (inverted y - top = 100%, bottom = 0%)
      const normalizedRelease = points[3].x - points[1].x; // Release end - decay end

      // Convert normalized values back to actual time values (in seconds)
      const attack = normalizedAttack * sampleDuration;
      const decay = normalizedDecay * sampleDuration;
      const release = normalizedRelease * sampleDuration;

      // Update global ADSR values with appropriate limits
      setAttack(Math.max(0.001, Math.min(sampleDuration, attack)));
      setDecay(Math.max(0.01, Math.min(sampleDuration * 2, decay)));
      setSustain(Math.max(0, Math.min(1.0, sustain)));
      setRelease(Math.max(0.01, Math.min(sampleDuration * 3, release)));
    },
    [sampleDuration]
  );

  // Handle mouse events
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      // Calculate graph dimensions dynamically
      const padding = 10;
      const graphWidth = width - 2 * padding;
      const graphHeight = height - 2 * padding;

      // Convert mouse coordinates to normalized graph coordinates (0-1)
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Check if click is within canvas bounds
      if (mouseX < 0 || mouseX > width || mouseY < 0 || mouseY > height) {
        return;
      }

      // Map to graph area (accounting for padding)
      const x = (mouseX - padding) / graphWidth;
      const y = (mouseY - padding) / graphHeight; // No inversion needed - coordinate systems match

      let closestPointIndex = -1;
      let closestDistance = Infinity;

      const sustainOffset = getSustainVisualOffset(controlPoints);

      for (let i = 0; i < controlPoints.length; i++) {
        const point = controlPoints[i];
        const visualOffset = point.type === "sustain" ? sustainOffset : 0;
        const hitX = Math.min(point.x + visualOffset, 1);

        // Mouse coordinates now directly match control point coordinate system
        const distance = Math.sqrt(
          Math.pow(x - hitX, 2) + Math.pow(y - point.y, 2)
        );

        // Find the closest point within click threshold
        if (distance < 0.25 && distance < closestDistance) {
          closestDistance = distance;
          closestPointIndex = i;
        }
      }

      // If we found a close enough point, select it
      if (closestPointIndex !== -1) {
        setDraggedPoint(closestPointIndex);
        setIsDragging(true);

        // Update the point's dragging state
        const newPoints = [...controlPoints];
        newPoints[closestPointIndex] = {
          ...controlPoints[closestPointIndex],
          dragging: true,
        };
        setControlPoints(newPoints);
      }
    },
    [controlPoints, width, height, getSustainVisualOffset]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDragging || draggedPoint === null) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      // Calculate graph dimensions dynamically
      const padding = 10;
      const graphWidth = width - 2 * padding;
      const graphHeight = height - 2 * padding;

      // Convert mouse coordinates to normalized graph coordinates (0-1)
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Check if mouse is within canvas bounds
      if (mouseX < 0 || mouseX > width || mouseY < 0 || mouseY > height) {
        return;
      }

      // Map to graph area (accounting for padding)
      let x = (mouseX - padding) / graphWidth;
      let y = (mouseY - padding) / graphHeight; // No inversion needed - coordinate systems match

      // Y coordinate matches control point coordinate system directly
      let yForControlPoints = y;

      // Clamp values within boundary
      x = Math.max(0, Math.min(1, x));
      yForControlPoints = Math.max(0, Math.min(1, yForControlPoints));

      // Update the dragged point
      const newPoints = [...controlPoints];
      const point = newPoints[draggedPoint];

      // Apply constraints based on point type and position
      switch (point.type) {
        case "attack":
          // Attack peak: can only move horizontally, stays at top
          x = Math.max(0, Math.min(newPoints[1].x - 0.01, x));
          yForControlPoints = 0; // Always stay at the top (maximum amplitude)
          break;
        case "decay":
          // Decay point: can only move horizontally, sustain level is controlled by sustain point
          x = Math.max(newPoints[0].x + 0.01, Math.min(0.8, x));
          yForControlPoints = newPoints[2].y; // Keep the same Y position as sustain point
          newPoints[2].x = x; // Sustain x locked to decay
          if (newPoints[3].x < x + 0.01) {
            newPoints[3].x = Math.min(1, x + 0.01);
          }
          break;
        case "sustain":
          // Sustain point: can only move vertically, sustain level is controlled by sustain point
          x = newPoints[1].x; // Lock to decay x
          yForControlPoints = Math.max(0, Math.min(1, yForControlPoints)); // Allow full range

          // Update both decay and sustain points to share the same Y value
          newPoints[1].y = yForControlPoints; // Decay point
          newPoints[2].y = yForControlPoints; // Sustain point
          break;
        case "release":
          // Release point: can only move horizontally, stays at zero
          x = Math.max(newPoints[1].x + 0.01, Math.min(1, x));
          yForControlPoints = 1;
          break;
      }

      newPoints[draggedPoint] = { ...point, x, y: yForControlPoints };
      setControlPoints(newPoints);

      // Update ADSR values based on control point positions
      updateADSRFromPoints(newPoints);

      // Immediately redraw with new points for responsive visual feedback
      requestAnimationFrame(() => {
        drawEnvelopeWithPoints(newPoints);
      });
    },
    [
      isDragging,
      draggedPoint,
      controlPoints,
      width,
      height,
      updateADSRFromPoints,
      drawEnvelopeWithPoints,
    ]
  );

  const handleMouseUp = useCallback(() => {
    if (draggedPoint !== null) {
      const newPoints = [...controlPoints];
      newPoints[draggedPoint] = { ...newPoints[draggedPoint], dragging: false };
      setControlPoints(newPoints);
    }
    setDraggedPoint(null);
    setIsDragging(false);
  }, [draggedPoint, controlPoints]);

  // Redraw when control points change
  useEffect(() => {
    drawEnvelope();
  }, [drawEnvelope]);

  return (
    <ADSREnvelopeContainer id="adsr-envelope">
      <ADSREnvelopeCanvas
        id="adsr-envelope-canvas"
        ref={canvasRef}
        width={width}
        height={height}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        $isDragging={isDragging}
      />
    </ADSREnvelopeContainer>
  );
};

export default ADSREnvelope;

const ADSREnvelopeContainer = styled.div`
  background: transparent;
  border-radius: 8px;
  border: none;
`;

const ADSREnvelopeCanvas = styled.canvas<{ $isDragging: boolean }>`
  display: block;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: ${({ $isDragging }) => ($isDragging ? "grabbing" : "grab")};

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }
`;
